import { API_BASE_URL } from '../config/apiConfig';

// ---------------------------------------------------------------------------
// In-memory access token
// AuthContext is the sole writer; other services call getAccessToken() to
// inject the Bearer header into protected requests.
// ---------------------------------------------------------------------------
let _accessToken = null;

export const setAccessToken = (token) => { _accessToken = token; };
export const getAccessToken = () => _accessToken;

// ---------------------------------------------------------------------------
// Internal fetch helper
// Handles 204 No Content and maps FastAPI's `detail` field to Error.message
// ---------------------------------------------------------------------------
async function authFetch(endpoint, body = null, withAuth = false) {
  const headers = { 'Content-Type': 'application/json' };
  if (withAuth && _accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }

  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: body !== null ? JSON.stringify(body) : undefined,
  });

  // 204 No Content — logout endpoints
  if (res.status === 204) return null;

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    // FastAPI uses `detail` for HTTPException messages
    throw new Error(data.detail || data.message || `Request failed (${res.status})`);
  }

  return data;
}

// ---------------------------------------------------------------------------
// Auth service methods
// ---------------------------------------------------------------------------
const userAuthService = {
  /**
   * POST /api/v1/auth/register
   * `website` is a honeypot field — always empty for real users, forwarded
   * so the backend can reject submissions that fill it in.
   * Returns UserResponse { id, email, is_active, is_admin, created_at, ... }
   */
  register: (email, password, website = '') =>
    authFetch('/api/v1/auth/register', { email, password, website }),

  /**
   * POST /api/v1/auth/login
   * Returns TokenResponse { access_token, refresh_token, token_type, expires_in }
   */
  login: (email, password) =>
    authFetch('/api/v1/auth/login', { email, password }),

  /**
   * POST /api/v1/auth/refresh
   * Returns TokenResponse with new access_token (same refresh_token)
   */
  refreshToken: (refresh_token) =>
    authFetch('/api/v1/auth/refresh', { refresh_token }),

  /**
   * POST /api/v1/auth/logout  → 204
   * Revokes the refresh token session on the server.
   */
  logout: (refresh_token) =>
    authFetch('/api/v1/auth/logout', { refresh_token }),

  /**
   * POST /api/v1/auth/logout-all  → 204
   * Revokes ALL sessions for the current user (requires valid access token).
   */
  logoutAll: () =>
    authFetch('/api/v1/auth/logout-all', {}, true),

  /**
   * POST /api/v1/auth/verify-email?token=xxx
   * Verifies the email address using the token from the verification link.
   */
  verifyEmail: async (token) => {
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || data.message || `Request failed (${res.status})`);
    return data;
  },

  /**
   * POST /api/v1/auth/password-reset-request
   * Always returns 202 — safe against email enumeration.
   */
  requestPasswordReset: (email) =>
    authFetch('/api/v1/auth/password-reset-request', { email }),

  /**
   * POST /api/v1/auth/password-reset
   * Completes the reset using the token from the email link.
   */
  resetPassword: (token, new_password) =>
    authFetch('/api/v1/auth/password-reset', { token, new_password }),
};

export default userAuthService;
